import { css } from "../../../styled-system/css";
import { token } from "../../../styled-system/tokens";

export const styles = {
  root: (isShort?: boolean) =>
    css({
      width: "100%",
      position: !isShort ? "relative" : "unset",
      tablet: {
        width: isShort ? "fit-content" : "100%",
      },
    }),

  form: () =>
    css({
      position: "relative",
      minWidth: "118px",
    }),

  withError: () =>
    css({
      "& label": { color: "red" },
      "& input": { color: "red" },
      "& > div": { color: "red" },
    }),

  disabled: () =>
    css({
      "& label": { color: "lightGrey" },
      "& input": {
        color: "lightGrey",
        _placeholder: { color: "lightGrey" },
      },
      "& fieldset": {
        backgroundColor: "backgroundGrey",
        borderColor: "disabledGrey",
        color: "lightGrey",
      },
      "& button": { cursor: "default" },
      "& > div": { color: "lightGrey" },
    }),

  success: () =>
    css({
      "& label": { color: "darkGreen" },
      "& fieldset": { borderColor: "darkGreen" },
      "& > div": { color: "darkGreen" },
    }),

  content: (showSelectSpace: boolean, disabled: boolean = false, hasError: boolean = false) =>
    css({
      display: "flex",
      alignItems: "center",
      gap: "baseUnit.mobile1",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: hasError ? "red" : "inputBorder.transparent",
      borderRadius: "baseUnit.mobile1",
      backgroundColor: "white",
      paddingX: "baseUnit.mobile2",
      paddingY: "baseUnit.mobile1",
      transition: "box-shadow .2s ease-in-out, border-color .2s ease-in-out",
      overflow: "hidden",
      cursor: disabled ? "default" : "pointer",
      boxShadow: showSelectSpace ? `0 0 0 2px white, 0 0 0 4px ${token("colors.intenseSeaBlue")}` : undefined,
      _focusWithin: {
        boxShadow: `0 0 0 2px white, 0 0 0 4px ${token("colors.intenseSeaBlue")}`,
      },
      _focusVisible: { outline: "none" },
    }),

  inputContainer: () =>
    css({
      display: "flex",
      flexDirection: "column",
      flex: "1 0 0",
      minWidth: 0,
    }),

  labelText: (_frontIcon: boolean) =>
    css({
      transition: "color .2s ease-in-out",
      color: "midnight",
      fontSize: "body.butterfly",
      lineHeight: "1.667",
      letterSpacing: "body.shrimp",
      fontWeight: 400,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),

  placeholderText: (isShort?: boolean, _customFrontIcon: boolean = false, disabled: boolean = false) =>
    css(
      isShort
        ? {
            fontSize: "body.cat",
            letterSpacing: "body.cat",
            color: disabled ? "lightGrey" : "headlineGrey",
            _focus: { outline: 0 },
          }
        : {
            fontSize: "body.mouse",
            lineHeight: "body.mouse",
            letterSpacing: "body.mouse",
            color: disabled ? "lightGrey" : "headlineGrey",
            _focus: { outline: 0 },
          },
    ),

  input: () =>
    css({
      transition: "color .2s ease-in-out",
      fontSize: "body.mouse",
      width: "100%",
      lineHeight: "body.mouse",
      letterSpacing: "body.mouse",
      color: "headlineGrey",
      margin: 0,
      border: "none",
      padding: 0,
      backgroundColor: "transparent",
      _focus: { outline: 0 },
      _placeholder: { color: "stdTextGrey" },
      "&::-ms-reveal": { display: "none" },
    }),

  info: (show: boolean) =>
    css({
      transition: "color .2s ease-in-out",
      color: "headlineGrey",
      fontSize: "body.butterfly",
      lineHeight: "1.334",
      letterSpacing: "body.butterfly",
      width: "100%",
      paddingTop: "4px",
      paddingX: "baseUnit.mobile2",
      visibility: show ? "visible" : "hidden",
      animation: show ? "fadeIn 0.2s forwards" : undefined,
    }),

  selectArrow: (showSelectSpace: boolean, disabled: boolean = false, _isShort?: boolean) =>
    css({
      transition: "transform 0.3s ease",
      backgroundImage: "var(--arrow-icon)",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundSize: "100%",
      flexShrink: 0,
      height: "baseUnit.mobile3",
      width: "baseUnit.mobile3",
      transform: showSelectSpace ? "rotate(180deg)" : undefined,
      pointerEvents: "none",
      opacity: disabled ? 0.4 : 1,
    }),

  selectSpace: (showSelectSpace: boolean, isShort?: boolean) =>
    css({
      marginTop: !isShort ? "28px" : undefined,
      boxShadow: "dropDownList",
      visibility: showSelectSpace ? "visible" : "hidden",
      paddingTop: "baseUnit.mobile1",
      paddingBottom: "baseUnit.mobile1",
      backgroundColor: "white",
      borderRadius: "baseUnit.mobile1",
      zIndex: "overShadow",
      transform: isShort ? "translateY(8px)" : "translateY(-24px)",
      userSelect: "none",
      position: "absolute",
      width: "100%",
      maxHeight: !isShort ? "180px" : undefined,
      overflow: "auto",
      tablet: {
        width: isShort ? "fit-content" : undefined,
      },
      "& li": {
        display: "flex",
        alignItems: "center",
        color: "headlineGrey",
        fontSize: "body.mouse",
        lineHeight: "body.mouse",
        letterSpacing: "body.mouse",
        paddingX: "baseUnit.mobile2",
        paddingY: "baseUnit.mobile1",
        minHeight: "baseUnit.mobile6",
        transition: "background-color 0.2s ease-in-out, color 0.2s ease-in-out",
        cursor: "pointer",
        _hover: {
          backgroundColor: "blueTint",
          color: "intenseSeaBlue",
        },
      },
    }),

  frontIcon: (isShort: boolean = false, disabled: boolean = false) =>
    css({
      display: "flex",
      alignSelf: "center",
      flexShrink: 0,
      border: "none",
      backgroundColor: "transparent",
      margin: 0,
      ...(disabled && { opacity: 0.4 }),
      "& svg": {
        width: isShort ? "baseUnit.mobile2" : "baseUnit.mobile3",
        height: isShort ? "baseUnit.mobile2" : "baseUnit.mobile3",
      },
    }),

  endIconWrapper: () =>
    css({
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "baseUnit.mobile1",
      flexShrink: 0,
    }),

  errorIcon: () =>
    css({
      alignSelf: "center",
      "& svg": {
        "& path": { fill: "red" },
      },
    }),

  endIcon: () =>
    css({
      "& button": {
        padding: 0,
        margin: 0,
        backgroundColor: "transparent",
        border: "none",
      },
      padding: 0,
      margin: 0,
      display: "flex",
      alignSelf: "center",
      border: "none",
      backgroundColor: "transparent",
      "& svg": {
        height: "baseUnit.mobile3",
        width: "baseUnit.mobile3",
      },
    }),
};
